import React, { useState } from 'react';

// Compose modal with the PQC toggle. When "Encrypt (post-quantum)" is on, the
// body is sealed via the crypto-service to the recipient's directory key before
// submission. This is the product's signature interaction.
export function Compose({ session, onClose, onSent }) {
  const { jmap, crypto, email } = session;
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [encrypt, setEncrypt] = useState(false);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true); setStatus(null);
    try {
      let textBody = body;
      let subjectOut = subject;
      if (encrypt) {
        // Look up recipient's public keys in the directory.
        const recip = await crypto.lookupRecipient(to);
        if (!recip?.kemPublicKey) {
          setStatus({ kind: 'warn', msg: `${to} has no PQC key — sending plaintext instead.` });
        } else if (session.identity?.sig) {
          // Seal to the recipient; the PQC envelope travels as the body.
          const sealed = await crypto.seal({
            from: email, to,
            plaintext: body,
            recipientKemPublicKey: recip.kemPublicKey,
            senderSigPrivateKey: session.identity.sig.privateKey,
          });
          textBody = '[KryptaMail post-quantum encrypted message]\n\n' +
            JSON.stringify(sealed.envelope);
          subjectOut = subject || '[KryptaMail encrypted]';
          setStatus({ kind: 'ok', msg: 'Sealed with PQC — sending…' });
        } else {
          setStatus({ kind: 'warn', msg: 'Your PQC key is locked — sending plaintext.' });
        }
      }
      // Real JMAP send: Email/set (draft) -> EmailSubmission/set.
      await jmap.send({ from: email, to, subject: subjectOut, textBody });
      setStatus({ kind: 'ok', msg: encrypt ? 'Encrypted & sent' : 'Sent' });
      setTimeout(onSent, 700);
    } catch (e) {
      setStatus({ kind: 'bad', msg: e.message });
      setBusy(false);
    }
  };

  return (
    <div className="km-modal-backdrop" onClick={onClose}>
      <div className="km-modal" onClick={(e) => e.stopPropagation()}>
        <div className="km-modal-head">
          <span>New message</span>
          <button className="km-btn-ghost" onClick={onClose}>✕</button>
        </div>

        <div className="km-compose-fields">
          <input className="km-compose-input" placeholder="To" value={to}
            onChange={(e) => setTo(e.target.value)} />
          <input className="km-compose-input" placeholder="Subject" value={subject}
            onChange={(e) => setSubject(e.target.value)} />
          <textarea className="km-compose-body" placeholder="Write your message…"
            value={body} onChange={(e) => setBody(e.target.value)} rows={12} />
        </div>

        <div className="km-compose-foot">
          <label className={`km-encrypt-toggle ${encrypt ? 'on' : ''}`}>
            <input type="checkbox" checked={encrypt}
              onChange={(e) => setEncrypt(e.target.checked)} />
            🔐 Encrypt (post-quantum)
          </label>
          {status && <span className={`km-compose-status ${status.kind}`}>{status.msg}</span>}
          <button className="km-btn-primary" onClick={send} disabled={busy || !to}>
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
